import { useCallback, useMemo, useState } from 'react';

import type { PosItem } from '../api/usePosItemsSearch';
import { buildLineKey, resolveLineKey } from '../utils/lineKeys';
import type {
  CommissionOverrideInput,
  ManualDiscountDraft,
  PosLineCommission,
  PosSaleLine,
  PosSaleLineFlags,
} from '../types';

const resolveInitialFlags = (item: PosItem, professionalId: number | null): PosSaleLineFlags => {
  const isService = item.item_type === 'service';
  const requiresAppointment = Boolean(item.service_detail?.is_appointment_based);
  const isPrepaid = Boolean(isService && item.service_detail?.is_subscription);
  const hasDefaultProfessional = professionalId != null;
  return {
    isPrepaid,
    requiresAppointment,
    hasPendingProfessional: isService && !hasDefaultProfessional,
  };
};

interface UsePosCartDraftOptions {
  resolveCommission: (item: PosItem, quantity: number, professionalId: number | null) => PosLineCommission;
  getPreferredProfessionalForItem: (itemId: number) => number | null;
  setPreferredProfessionalForItem: (itemId: number, professionalId: number | null) => void;
}

export interface PosQuoteDraftLineInput {
  itemId?: number | null;
  item_id?: number | null;
  variantId?: number | null;
  variant_id?: number | null;
  name: string;
  sku?: string | null;
  itemType?: PosItem['item_type'] | null;
  item_type?: PosItem['item_type'] | null;
  quantity: number | string;
  unitPrice: number | string;
  unit_price?: number | string;
  currency_code?: string | null;
  professionalId?: number | null;
  professional_id?: number | null;
  lineKey?: string | null;
  line_key?: string | null;
  notes?: string | null;
  manualDiscount?: ManualDiscountDraft | null;
  manual_discount?: ManualDiscountDraft | null;
  commission?: PosLineCommission | null;
  flags?: PosSaleLineFlags | null;
  options?: Record<string, unknown> | null;
}

export interface PosQuoteDraftInput {
  customerId?: number | null;
  items?: PosQuoteDraftLineInput[];
  orderDiscount?: ManualDiscountDraft | null;
}

export interface PosCartDraftApi {
  lines: PosSaleLine[];
  totalLines: number;
  hasLines: boolean;
  updatedAt: number;
  orderDiscount: ManualDiscountDraft | null;
  addItem: (item: PosItem) => PosSaleLine | null;
  updateQuantity: (lineKey: string, quantity: number) => PosSaleLine | null;
  removeLine: (lineKey: string) => PosSaleLine | null;
  updateProfessional: (lineKey: string, professionalId: number | null) => PosSaleLine | null;
  updateVariant: (lineKey: string, variantId: number | null) => PosSaleLine | null;
  updateNotes: (lineKey: string, notes: string) => PosSaleLine | null;
  updateSubscriptionUsage: (
    lineKey: string,
    payload: Partial<
      Pick<
        PosSaleLine,
        'useSubscription' | 'subscriptionId' | 'subscriptionAvailable' | 'subscriptionUnlimited' | 'subscriptionName'
      >
    >,
  ) => PosSaleLine | null;
  overrideCommission: (itemId: number, override: CommissionOverrideInput) => void;
  applyCommissionPreview: (entries: Array<{ item_id: number; amount: number; agent_id?: number | null; agreement_id?: number | null }>) => void;
  applyLineDiscount: (itemId: number, discount: ManualDiscountDraft | null) => void;
  applyOrderDiscount: (discount: ManualDiscountDraft | null) => void;
  clearOrderDiscount: () => void;
  loadDraft: (draft: PosQuoteDraftInput) => void;
  resetCart: () => void;
}

interface SaleDraftState {
  lines: PosSaleLine[];
  orderDiscount: ManualDiscountDraft | null;
  updatedAt: number;
}

const createInitialState = (): SaleDraftState => ({
  lines: [],
  orderDiscount: null,
  updatedAt: Date.now(),
});

export const usePosCartDraft = ({
  resolveCommission,
  getPreferredProfessionalForItem,
  setPreferredProfessionalForItem,
}: UsePosCartDraftOptions): PosCartDraftApi => {
  const [draft, setDraft] = useState<SaleDraftState>(() => createInitialState());

  const resolvePreferredProfessionalId = useCallback(
    (item: PosItem): number | null => {
      const storedProfessional = getPreferredProfessionalForItem(item.id);
      if (storedProfessional != null && item.professionals?.some((professional) => professional.id === storedProfessional)) {
        return storedProfessional;
      }
      return item.service_detail?.default_professional_id ?? null;
    },
    [getPreferredProfessionalForItem],
  );

  const addItem = useCallback(
    (item: PosItem): PosSaleLine | null => {
      if (item.is_parent) {
        return null;
      }

      let createdLine: PosSaleLine | null = null;
      setDraft((previous) => {
        const lineKey = buildLineKey(item.id, null);
        const professionalId = resolvePreferredProfessionalId(item);
        const existingIndex = previous.lines.findIndex((line) => resolveLineKey(line) === lineKey);

        if (existingIndex >= 0) {
          const updatedLines = [...previous.lines];
          const existingLine = updatedLines[existingIndex];
          const nextQuantity = existingLine.quantity + 1;
          const activeProfessionalId =
            existingLine.professionalId ?? professionalId ?? null;
          const shouldRecalculate =
            !existingLine.commission || existingLine.commission.mode === 'calculated';
          const recalculated = shouldRecalculate
            ? resolveCommission(item, nextQuantity, activeProfessionalId)
            : existingLine.commission;
          const mergedLine: PosSaleLine = {
            ...existingLine,
            quantity: nextQuantity,
            commission:
              recalculated ?? resolveCommission(item, nextQuantity, activeProfessionalId),
          };
          updatedLines[existingIndex] = mergedLine;
          createdLine = mergedLine;
          return { ...previous, lines: updatedLines, updatedAt: Date.now() };
        }

        const commission = resolveCommission(item, 1, professionalId ?? null);
        const flags = resolveInitialFlags(item, professionalId);
        const newLine: PosSaleLine = {
          item,
          quantity: 1,
          commission,
          variantId: null,
          professionalId,
          lineKey,
          flags,
          notes: null,
          useSubscription: false,
          subscriptionId: null,
          subscriptionAvailable: null,
          subscriptionUnlimited: false,
          subscriptionName: null,
        };
        createdLine = newLine;
        return {
          ...previous,
          lines: [...previous.lines, newLine],
          updatedAt: Date.now(),
        };
      });
      return createdLine!;
    },
    [resolveCommission, resolvePreferredProfessionalId],
  );

  const updateQuantity = useCallback(
    (lineKey: string, quantity: number): PosSaleLine | null => {
      let updated: PosSaleLine | null = null;
      setDraft((previous) => {
        const updatedLines = previous.lines.map((line) => {
          if (resolveLineKey(line) !== lineKey) {
            return line;
          }
          const safeQuantity = Number.isFinite(quantity) ? Math.max(0, quantity) : 0;
          const shouldRecalculate = !line.commission || line.commission.mode === 'calculated';
          const activeProfessionalId = line.professionalId ?? null;
          const recalculated = shouldRecalculate
            ? resolveCommission(line.item, safeQuantity, activeProfessionalId)
            : line.commission;
          updated = {
            ...line,
            quantity: safeQuantity,
            commission:
              recalculated ?? resolveCommission(line.item, safeQuantity, activeProfessionalId),
          };
          return updated;
        });
        return { ...previous, lines: updatedLines, updatedAt: Date.now() };
      });
      return updated;
    },
    [resolveCommission],
  );

  const removeLine = useCallback((lineKey: string): PosSaleLine | null => {
    let removedLine: PosSaleLine | null = null;
    setDraft((previous) => {
      const nextLines = previous.lines.filter((line) => {
        const isMatch = resolveLineKey(line) === lineKey;
        if (isMatch) {
          removedLine = line;
        }
        return !isMatch;
      });
      return {
        lines: nextLines,
        orderDiscount: nextLines.length === 0 ? null : previous.orderDiscount,
        updatedAt: Date.now(),
      };
    });
    return removedLine;
  }, []);

  const updateProfessional = useCallback(
    (lineKey: string, professionalId: number | null): PosSaleLine | null => {
      let updated: PosSaleLine | undefined;
      setDraft((previous) => {
        const updatedLines = previous.lines.map((line) => {
          if (resolveLineKey(line) !== lineKey) {
            return line;
          }
          const activeProfessionalId = professionalId ?? null;
          const shouldRecalculate = !line.commission || line.commission.mode === 'calculated';
          const commission = shouldRecalculate
            ? resolveCommission(line.item, line.quantity, activeProfessionalId)
            : line.commission;
          const nextFlags: PosSaleLineFlags = {
            ...(line.flags ?? {}),
            hasPendingProfessional: line.item.item_type === 'service' ? professionalId == null : false,
          };
          updated = {
            ...line,
            professionalId,
            commission,
            flags: nextFlags,
          };
          return updated;
        });
        return { ...previous, lines: updatedLines, updatedAt: Date.now() };
      });
      if (updated) {
        setPreferredProfessionalForItem(updated.item.id, professionalId);
      }
      return updated ?? null;
    },
    [resolveCommission, setPreferredProfessionalForItem],
  );

  const updateVariant = useCallback(
    (lineKey: string, variantId: number | null): PosSaleLine | null => {
      let updated: PosSaleLine | undefined;
      setDraft((previous) => {
        const currentIndex = previous.lines.findIndex((line) => resolveLineKey(line) === lineKey);
        if (currentIndex < 0) {
          return previous;
        }
        const currentLine = previous.lines[currentIndex];
        const nextKey = buildLineKey(currentLine.item.id, variantId);
        const duplicateIndex = previous.lines.findIndex(
          (line, index) => index !== currentIndex && resolveLineKey(line) === nextKey,
        );

        const updatedLines = [...previous.lines];
        const updatedLine: PosSaleLine = {
          ...currentLine,
          variantId,
          lineKey: nextKey,
        };

        if (duplicateIndex >= 0) {
          const duplicate = updatedLines[duplicateIndex];
          const mergedQuantity = duplicate.quantity + updatedLine.quantity;
          const shouldRecalculate = !duplicate.commission || duplicate.commission.mode === 'calculated';
          const activeProfessionalId = duplicate.professionalId ?? null;
          const recalculated = shouldRecalculate
            ? resolveCommission(duplicate.item, mergedQuantity, activeProfessionalId)
            : duplicate.commission;
          updatedLines[duplicateIndex] = {
            ...duplicate,
            quantity: mergedQuantity,
            commission:
              recalculated ?? resolveCommission(duplicate.item, mergedQuantity, activeProfessionalId),
          };
          updatedLines.splice(currentIndex, 1);
          updated = updatedLines[duplicateIndex];
        } else {
          updatedLines[currentIndex] = updatedLine;
          updated = updatedLine;
        }

        return { ...previous, lines: updatedLines, updatedAt: Date.now() };
      });
      return updated ?? null;
    },
    [resolveCommission],
  );

  const updateNotes = useCallback((lineKey: string, notes: string): PosSaleLine | null => {
    let updated: PosSaleLine | null = null;
    setDraft((previous) => {
      const updatedLines = previous.lines.map((line) => {
        if (resolveLineKey(line) !== lineKey) {
          return line;
        }
        updated = {
          ...line,
          notes,
        };
        return updated;
      });
      return { ...previous, lines: updatedLines, updatedAt: Date.now() };
    });
    return updated;
  }, []);

  const updateSubscriptionUsage = useCallback(
    (
      lineKey: string,
      payload: Partial<
        Pick<
          PosSaleLine,
          'useSubscription' | 'subscriptionId' | 'subscriptionAvailable' | 'subscriptionUnlimited' | 'subscriptionName'
        >
      >,
    ): PosSaleLine | null => {
      let updated: PosSaleLine | null = null;
      setDraft((previous) => {
        const updatedLines = previous.lines.map((line) => {
          if (resolveLineKey(line) !== lineKey) {
            return line;
          }
          updated = { ...line, ...payload };
          return updated;
        });
        return { ...previous, lines: updatedLines, updatedAt: Date.now() };
      });
      return updated;
    },
    [],
  );

  const overrideCommission = useCallback(
    (itemId: number, override: CommissionOverrideInput) => {
      setDraft((previous) => {
        const updatedLines = previous.lines.map((line) => {
          if (line.item.id !== itemId) {
            return line;
          }

          const automatic = resolveCommission(line.item, line.quantity, line.professionalId ?? null);
          const previousAmount = line.commission?.amount ?? automatic.amount;
          const calculatedAmount =
            line.commission?.calculatedAmount ?? automatic.calculatedAmount ?? automatic.amount;
          const agreementLabel = line.commission?.agreementLabel ?? automatic.agreementLabel;
          const capReached = line.commission?.capReached ?? automatic.capReached ?? null;
          const manualMessage = override.note ? `${override.reason} · ${override.note}` : override.reason;

          let commission: PosLineCommission;
          if (override.mode === 'manual') {
            commission = {
              mode: 'manual',
              amount: override.amount ?? 0,
              agentId: override.agentId ?? null,
              agentName:
                override.agentId != null
                  ? `Agente #${override.agentId}`
                  : line.commission?.agentName ?? 'Sin asignar',
              agreementLabel,
              capReached,
              message: manualMessage || 'Manual',
              calculatedAmount,
              previousAmount,
              overrideReason: override.reason,
              overrideNote: override.note ?? null,
            };
          } else if (override.mode === 'none') {
            commission = {
              mode: 'none',
              amount: 0,
              agentId: null,
              agentName: null,
              agreementLabel,
              capReached: null,
              message: manualMessage || 'Sin comisión',
              calculatedAmount,
              previousAmount,
              overrideReason: override.reason,
              overrideNote: override.note ?? null,
            };
          } else {
            commission = {
              ...automatic,
              agentId: override.agentId ?? automatic.agentId ?? null,
              agentName:
                override.agentId != null
                  ? `Agente #${override.agentId}`
                  : automatic.agentName ?? 'Automático',
              previousAmount,
              calculatedAmount: automatic.calculatedAmount ?? automatic.amount,
              message: 'Comisión automática restablecida',
              overrideReason: null,
              overrideNote: null,
            };
          }

          return { ...line, commission };
        });
        return { ...previous, lines: updatedLines, updatedAt: Date.now() };
      });
    },
    [resolveCommission],
  );

  const applyCommissionPreview = useCallback(
    (entries: Array<{ item_id: number; amount: number; agent_id?: number | null; agreement_id?: number | null }>) => {
      const byItem = new Map(entries.map((entry) => [entry.item_id, entry]));
      setDraft((previous) => ({
        ...previous,
        lines: previous.lines.map((line) => {
          if (line.commission?.mode === 'manual') return line;
          const preview = byItem.get(line.item.id);
          if (!preview?.agreement_id) {
            return {
              ...line,
              commission: {
                mode: 'none',
                amount: 0,
                agentId: null,
                agreementId: null,
                agreementLabel: null,
                calculatedAmount: 0,
              },
            };
          }
          const amount = Number(preview.amount ?? 0);
          return {
            ...line,
            commission: {
              mode: 'calculated',
              amount,
              agentId: preview.agent_id ?? null,
              agreementId: preview.agreement_id,
              agreementLabel: `Convenio #${preview.agreement_id}`,
              calculatedAmount: amount,
            },
          };
        }),
        updatedAt: Date.now(),
      }));
    },
    [],
  );

  const applyLineDiscount = useCallback((itemId: number, discount: ManualDiscountDraft | null) => {
    setDraft((previous) => {
      const updatedLines = previous.lines.map((line) =>
        line.item.id === itemId
          ? {
              ...line,
              manualDiscount: discount ?? undefined,
            }
          : line,
      );
      return {
        ...previous,
        lines: updatedLines,
        updatedAt: Date.now(),
      };
    });
  }, []);

  const applyOrderDiscount = useCallback((discount: ManualDiscountDraft | null) => {
    setDraft((previous) => ({
      ...previous,
      orderDiscount: discount ?? null,
      updatedAt: Date.now(),
    }));
  }, []);

  const clearOrderDiscount = useCallback(() => {
    setDraft((previous) => ({
      ...previous,
      orderDiscount: null,
      updatedAt: Date.now(),
    }));
  }, []);

  const loadDraft = useCallback((input: PosQuoteDraftInput) => {
    const nextLines = (input.items ?? [])
      .map((entry, index): PosSaleLine | null => {
        const itemId = Number(entry.itemId ?? entry.item_id ?? index + 1);
        const quantity = Number(entry.quantity ?? 1);
        const unitPrice = Number(entry.unitPrice ?? entry.unit_price ?? 0);
        if (!Number.isFinite(itemId) || !Number.isFinite(quantity) || quantity <= 0) {
          return null;
        }
        const professionalId = entry.professionalId ?? entry.professional_id ?? null;
        const variantId = entry.variantId ?? entry.variant_id ?? null;
        const itemType = entry.itemType ?? entry.item_type ?? 'product';
        const options = entry.options ?? {};
        const item: PosItem = {
          id: itemId,
          name: entry.name,
          sku: entry.sku ?? null,
          item_type: itemType,
          price: {
            value: Number.isFinite(unitPrice) ? unitPrice : 0,
            currency: entry.currency_code ?? 'ARS',
          },
          category_ids: [],
          flags: {
            is_active: true,
            is_for_sale: true,
            is_visible_web: false,
            is_digital: false,
            is_insumo: false,
          },
          // Drafts created by the previous POS selector stored the parent ID
          // together with a variant ID. Keep those lines fail-closed until the
          // quote can be rebuilt with the real child ID.
          is_parent: variantId != null,
          children: [],
          professionals: null,
          service_detail: null,
        };
        const lineKey = entry.lineKey ?? entry.line_key ?? buildLineKey(item.id, variantId);
        return {
          item,
          quantity,
          commission: entry.commission ?? resolveCommission(item, quantity, professionalId),
          variantId,
          professionalId,
          lineKey,
          flags: entry.flags ?? resolveInitialFlags(item, professionalId),
          notes: entry.notes ?? null,
          manualDiscount: entry.manualDiscount ?? entry.manual_discount ?? undefined,
          useSubscription: Boolean(options.useSubscription),
          subscriptionId: Number(options.subscriptionId ?? 0) || null,
          subscriptionAvailable: Number(options.subscriptionAvailable ?? 0) || null,
          subscriptionUnlimited: Boolean(options.subscriptionUnlimited),
          subscriptionName: typeof options.subscriptionName === 'string' ? options.subscriptionName : null,
        };
      })
      .filter((line): line is PosSaleLine => Boolean(line));

    setDraft({
      lines: nextLines,
      orderDiscount: input.orderDiscount ?? null,
      updatedAt: Date.now(),
    });
  }, [resolveCommission]);

  const resetCart = useCallback(() => {
    setDraft(createInitialState());
  }, []);

  const lines = draft.lines;
  const totalLines = lines.length;
  const hasLines = totalLines > 0;
  const orderDiscount = draft.orderDiscount;
  const updatedAt = draft.updatedAt;

  return useMemo(
    () => ({
      lines,
      totalLines,
      hasLines,
      updatedAt,
      orderDiscount,
      addItem,
      updateQuantity,
      removeLine,
      updateProfessional,
      updateVariant,
      updateNotes,
      updateSubscriptionUsage,
    overrideCommission,
    applyCommissionPreview,
      applyLineDiscount,
      applyOrderDiscount,
      clearOrderDiscount,
      loadDraft,
      resetCart,
    }),
    [
      addItem,
      applyLineDiscount,
      applyOrderDiscount,
      clearOrderDiscount,
      loadDraft,
      hasLines,
      lines,
      orderDiscount,
      overrideCommission,
      updateSubscriptionUsage,
      removeLine,
      resetCart,
      totalLines,
      updateNotes,
      updateProfessional,
      updateQuantity,
      updateVariant,
      updatedAt,
    ],
  );
};

export default usePosCartDraft;
